import { type BibleViewOptions } from '@soulhx/fs-common';
import { SHXButton, SHXCol, SHXRow, StandardButton } from '@soulhx/ui-common';
import React, { useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface IBibleViewerOptions {
  dirtyOptions: boolean;
  updateDefaults?: (newVal: BibleViewOptions) => void;
  updateLocalDefaults?: (newVal: BibleViewOptions) => void;
  options: BibleViewOptions;
  update: (newVal: BibleViewOptions) => void;
  forceShowOptions?: boolean;
}
export const BibleViewerOptions = ({
  dirtyOptions,
  updateDefaults,
  options: initialOptions,
  update,
  forceShowOptions = false,
  updateLocalDefaults,
}: IBibleViewerOptions) => {
  const { t } = useTranslation(['bibleviewer']);
  const [showOptions, setShowOptions] = useState(forceShowOptions);
  const [options, setOptions] = useState(initialOptions);

  const setOption = useCallback(
    (settingName: keyof BibleViewOptions, settingValue: boolean) => {
      const newOptions: BibleViewOptions = { ...options, [settingName]: settingValue };
      setOptions(newOptions);
      update(newOptions);
    },
    [options, update]
  );

  return (
    <SHXRow className="bibleviewer-options-container">
      <SHXCol className="m-0 p-0 text-end">
        <SHXRow className="text-start">
          {(showOptions || forceShowOptions) && (
            <>
              <SHXCol>
                <Form.Check
                  type="checkbox"
                  label={t('bibleviewer:options.showVersesLabel')}
                  checked={options.verseNums}
                  onChange={(e) => setOption('verseNums', e.currentTarget.checked)}
                />
              </SHXCol>
              <SHXCol>
                <Form.Check
                  type="checkbox"
                  label={t('bibleviewer:options.showFootnotesLabel')}
                  checked={options.footnotes}
                  onChange={(e) => setOption('footnotes', e.currentTarget.checked)}
                />
              </SHXCol>
              <SHXCol>
                <Form.Check
                  type="checkbox"
                  label={t('bibleviewer:options.showHeadingsLabel')}
                  checked={options.headings}
                  onChange={(e) => setOption('headings', e.currentTarget.checked)}
                />
              </SHXCol>
              <SHXCol>
                <Form.Check
                  type="checkbox"
                  label={t('bibleviewer:options.showCrossRefsLabel')}
                  checked={options.crossRef}
                  onChange={(e) => setOption('crossRef', e.currentTarget.checked)}
                />
              </SHXCol>
              <SHXCol>
                <Form.Check
                  type="checkbox"
                  label={t('bibleviewer:options.showRedLetters')}
                  checked={options.redLetters}
                  onChange={(e) => setOption('redLetters', e.currentTarget.checked)}
                />
              </SHXCol>
              {dirtyOptions && updateDefaults && (
                <SHXCol className="text-end">
                  <SHXButton
                    size="sm"
                    variant="outline-success"
                    onClick={() => {
                      updateDefaults(options);
                      if (!updateLocalDefaults) {
                        setShowOptions(false);
                      }
                    }}
                  >
                    {t('bibleviewer:options.setDefaultBtn')}
                  </SHXButton>
                </SHXCol>
              )}
              {dirtyOptions && updateLocalDefaults && (
                <SHXCol className="text-end">
                  <SHXButton
                    size="sm"
                    variant="outline-success"
                    onClick={() => {
                      updateLocalDefaults(options);
                      if (!updateDefaults) {
                        setShowOptions(false);
                      }
                    }}
                  >
                    {t('bibleviewer:options.saveLocalOptions')}
                  </SHXButton>
                </SHXCol>
              )}
            </>
          )}

          {!forceShowOptions && (
            <SHXCol xs={showOptions ? 1 : 12} className="text-end">
              <StandardButton
                type="settings"
                done={showOptions}
                onClick={() => setShowOptions((current) => !current)}
              />
            </SHXCol>
          )}
        </SHXRow>
      </SHXCol>
    </SHXRow>
  );
};
